/**
 * A session beside a document (RFC 0301 §3a).
 *
 * The record and nothing else: who is paired to what, and whether it may edit.
 * What a pairing DOES (the gate, a firing, a claim) reads this and lives beside
 * the delivery it performs.
 *
 * ON THE DOCUMENT (RFC 0339 3b), because `mayEdit` is the document's rule about
 * who may write it: one switch in one place, read off one doc. The session's
 * project keeps an index (`pairingIndex`) so the holdings bar lists a session's
 * pairings in one read, and the project's old `pairings` map is read after the
 * document until the move (3e) has emptied it.
 *
 * ONE PAIRING PER SESSION AND DOCUMENT. Pairing twice returns the pairing that
 * exists, so a double click is not two sessions' worth of turns.
 */

import {
  type DocHandle,
  isValidDocumentId,
  type Repo,
} from '@automerge/automerge-repo/slim'
import { v4 as uuidv4 } from 'uuid'
import { mapWithConflicts, readyHandle } from '../comments/store'
import type { DocumentDoc } from '../documents/types'
import {
  changeProject,
  plainProjectEntry,
  projectString,
} from '../project/scalars'
import type { Pairing, PairingIndexEntry, ProjectDoc } from '../project/types'
import { imm, sameStr } from '../stored-string'
/** A turn at most every thirty seconds on one document (§3g). */
export const DEFAULT_COOLDOWN_MS = 30_000
/** Twelve turns an hour per pairing (§3g). A pairing left running overnight
 *  costs a bounded number of turns, not one per edit. */
export const DEFAULT_MAX_PER_HOUR = 12

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/**
 * The pairings in a doc's `pairings` map, as plain objects, optionally
 * narrowed to one session or one document. Reads a `DocumentDoc` or, for what
 * has not moved yet, a `ProjectDoc`. Disabled pairings are left out unless
 * asked for, because every reader but a settings screen wants the ones that act.
 */
export function projectPairings(
  doc: unknown,
  filter: {
    sessionId?: string
    documentId?: string
    includeDisabled?: boolean
  } = {},
): Pairing[] {
  // Every concurrent value of the field (RFC 0339 review): two devices that
  // each paired a session to a page with no pairings yet made two maps, and
  // the one that lost the merge still holds a real edit rule.
  const map = mapWithConflicts(doc, 'pairings')
  const out: Pairing[] = []
  for (const raw of Object.values(map)) {
    if (!raw || typeof raw !== 'object') continue
    const p = raw as Record<string, unknown>
    const sessionId = String(p.sessionId ?? '')
    const documentId = String(p.documentId ?? '')
    if (!sessionId || !documentId) continue
    if (filter.sessionId && sessionId !== filter.sessionId) continue
    if (filter.documentId && documentId !== filter.documentId) continue
    const enabled = p.enabled !== false
    if (!enabled && !filter.includeDisabled) continue
    out.push({
      id: String(p.id ?? ''),
      sessionId,
      documentId,
      // Anything but an explicit false may edit: the default is on (§3a).
      mayEdit: p.mayEdit !== false,
      ...(projectString(p.instruction)
        ? { instruction: projectString(p.instruction) }
        : {}),
      enabled,
      createdBy: String(p.createdBy ?? ''),
      createdAt: Number(p.createdAt ?? 0),
      cooldownMs: Number(p.cooldownMs ?? DEFAULT_COOLDOWN_MS),
      maxPerHour: Number(p.maxPerHour ?? DEFAULT_MAX_PER_HOUR),
    })
  }
  return out.sort((a, b) => a.createdAt - b.createdAt)
}

/** The pairing of one session to one document in one doc's map, or null. */
export function pairingFor(
  doc: unknown,
  sessionId: string,
  documentId: string,
): Pairing | null {
  return projectPairings(doc, { sessionId, documentId })[0] ?? null
}

/**
 * A session's pairings as the holdings bar lists them, from its project alone
 * (RFC 0339 3d): the index, then what has not moved. `mayEdit` is only known
 * for the ones that have not moved; for the rest it is read off the document.
 */
export function sessionPairingRows(
  projectDoc: unknown,
  sessionId: string,
): (PairingIndexEntry & { id: string; mayEdit?: boolean })[] {
  const out = new Map<
    string,
    PairingIndexEntry & { id: string; mayEdit?: boolean }
  >()
  const index = mapWithConflicts<PairingIndexEntry>(projectDoc, 'pairingIndex')
  for (const [id, raw] of Object.entries(index)) {
    if (!raw || String(raw.sessionId ?? '') !== sessionId) continue
    out.set(id, {
      id,
      sessionId,
      documentId: String(raw.documentId ?? ''),
      enabled: raw.enabled !== false,
    })
  }
  for (const p of projectPairings(projectDoc, {
    sessionId,
    includeDisabled: true,
  })) {
    out.set(p.id, {
      id: p.id,
      sessionId,
      documentId: p.documentId,
      enabled: p.enabled,
      mayEdit: p.mayEdit,
    })
  }
  return [...out.values()].filter((r) => r.documentId && r.enabled)
}

/** Where a pairing was found. */
interface PairingHome {
  handle: DocHandle<DocumentDoc> | DocHandle<ProjectDoc>
  pairing: Pairing
  moved: boolean
}

/**
 * The pairing of a session to a document, on the document first and then on
 * the project's leftovers. Null when neither has one.
 */
async function findPairing(
  repo: Repo,
  projectId: string,
  sessionId: string,
  documentId: string,
  includeDisabled: boolean,
): Promise<PairingHome | null> {
  const doc = await readyHandle<DocumentDoc>(repo, documentId)
  const onDoc = projectPairings(doc?.doc(), {
    sessionId,
    documentId,
    includeDisabled,
  })[0]
  if (doc && onDoc) return { handle: doc, pairing: onDoc, moved: true }
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  const left = projectPairings(project?.doc(), {
    sessionId,
    documentId,
    includeDisabled,
  })[0]
  if (project && left) return { handle: project, pairing: left, moved: false }
  return null
}

/** A pairing by id: through the index to its document, then the leftovers. */
async function findPairingById(
  repo: Repo,
  projectId: string,
  pairingId: string,
): Promise<PairingHome | null> {
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  const entry = mapWithConflicts<PairingIndexEntry>(
    project?.doc(),
    'pairingIndex',
  )[pairingId]
  if (entry?.documentId) {
    const doc = await readyHandle<DocumentDoc>(repo, String(entry.documentId))
    const p = projectPairings(doc?.doc(), { includeDisabled: true }).find(
      (x) => x.id === pairingId,
    )
    if (doc && p) return { handle: doc, pairing: p, moved: true }
  }
  const left = projectPairings(project?.doc(), { includeDisabled: true }).find(
    (x) => x.id === pairingId,
  )
  if (project && left) return { handle: project, pairing: left, moved: false }
  return null
}

/** Index a pairing on the session's project, after the pairing (3d). */
async function indexPairing(
  repo: Repo,
  projectId: string,
  pairing: Pick<Pairing, 'id' | 'sessionId' | 'documentId' | 'enabled'>,
): Promise<void> {
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  if (!project) return
  // Ids, written as one op each rather than one per character: see
  // `stored-string`. The guard below compares characters for the same reason —
  // two wrappers holding the same id are different objects, so `===` would
  // rewrite this row on every pass.
  const entry: PairingIndexEntry = {
    sessionId: imm(pairing.sessionId),
    documentId: imm(pairing.documentId),
    enabled: pairing.enabled,
  }
  const stored = project.doc()?.pairingIndex?.[pairing.id]
  const now = stored && plainProjectEntry(stored)
  if (
    now &&
    sameStr(now.sessionId, entry.sessionId) &&
    sameStr(now.documentId, entry.documentId) &&
    now.enabled === entry.enabled
  ) {
    return
  }
  changeProject(project, (d: Draft) => {
    if (!d.pairingIndex) d.pairingIndex = {}
    d.pairingIndex[pairing.id] = entry
  })
}

/** Set fields on a pairing where it was found, and keep the index honest. */
async function changePairing(
  repo: Repo,
  projectId: string,
  home: PairingHome,
  fields: Partial<Pick<Pairing, 'enabled' | 'mayEdit'>>,
): Promise<void> {
  ;(home.handle as DocHandle<DocumentDoc | ProjectDoc>).change((d: Draft) => {
    if (!d.pairings) d.pairings = {}
    // Found only in a map that lost a concurrent merge: copy it into the
    // winning one, which is where every reader looks first.
    if (!d.pairings[home.pairing.id]) d.pairings[home.pairing.id] = home.pairing
    const p = d.pairings[home.pairing.id]
    for (const [k, v] of Object.entries(fields)) p[k] = v
  })
  if (home.moved) {
    await indexPairing(repo, projectId, { ...home.pairing, ...fields })
  }
}

/**
 * Pair a session to a document. Returns the pairing id. `projectId` is the
 * SESSION's project, which holds the index entry.
 *
 * Pairing what is already paired returns that pairing and re-enables it, and
 * leaves `mayEdit` as the person last set it: pairing again is not a way to
 * reset a boundary somebody chose.
 */
export async function pairSession(
  repo: Repo,
  projectId: string,
  opts: {
    sessionId: string
    documentId: string
    createdBy: string
    mayEdit?: boolean
    instruction?: string
  },
): Promise<string> {
  const existing = await findPairing(
    repo,
    projectId,
    opts.sessionId,
    opts.documentId,
    true,
  )
  if (existing) {
    if (!existing.pairing.enabled) {
      await changePairing(repo, projectId, existing, { enabled: true })
    }
    return existing.pairing.id
  }
  const id = uuidv4()
  const pairing: Pairing = {
    id,
    sessionId: opts.sessionId,
    documentId: opts.documentId,
    mayEdit: opts.mayEdit ?? true,
    ...(opts.instruction ? { instruction: opts.instruction } : {}),
    enabled: true,
    createdBy: opts.createdBy,
    createdAt: Date.now(),
    cooldownMs: DEFAULT_COOLDOWN_MS,
    maxPerHour: DEFAULT_MAX_PER_HOUR,
  }
  // A code file (`code:<ref>:<path>`) is not a document and has no page to
  // hold the rule, so its pairing stays on the session's project, where
  // `findPairing` looks after the page.
  if (!isValidDocumentId(opts.documentId)) {
    const project = await readyHandle<ProjectDoc>(repo, projectId)
    if (!project) throw new Error(`project ${projectId} is not available here`)
    changeProject(project, (d: Draft) => {
      if (!d.pairings) d.pairings = {}
      d.pairings[id] = pairing
    })
    return id
  }
  const doc = await readyHandle<DocumentDoc>(repo, opts.documentId)
  if (!doc) throw new Error(`document ${opts.documentId} is not available here`)
  doc.change((d: Draft) => {
    if (!d.pairings) d.pairings = {}
    d.pairings[id] = pairing
  })
  await indexPairing(repo, projectId, pairing)
  return id
}

/**
 * Set whether a pairing may edit. A person's act: nothing in the agent's tool
 * set calls this, which is how "the agent can never change that" holds (§3a).
 */
export async function setPairingMayEdit(
  repo: Repo,
  projectId: string,
  pairingId: string,
  mayEdit: boolean,
): Promise<void> {
  const home = await findPairingById(repo, projectId, pairingId)
  if (home) await changePairing(repo, projectId, home, { mayEdit })
}

/**
 * Unpair. DISABLED, not deleted, because opening a document beside a session
 * pairs it (`pairOnOpen`), and a deleted pairing would come back the next time
 * the pane opened. A disabled one is the person's answer, and it stays.
 */
export async function unpairSession(
  repo: Repo,
  projectId: string,
  pairingId: string,
): Promise<void> {
  const home = await findPairingById(repo, projectId, pairingId)
  if (home) await changePairing(repo, projectId, home, { enabled: false })
}

/**
 * Pair because a document was put on screen beside a session (§3a).
 *
 * Putting them side by side IS the gesture: nobody should have to find a second
 * button to say what the layout already says. Never overrides a person, so a
 * pairing that exists in any state, including one they turned off, is left
 * exactly as it is. Returns the new pairing id, or null when none was written.
 */
export async function pairOnOpen(
  repo: Repo,
  projectId: string,
  sessionId: string,
  documentId: string,
): Promise<string | null> {
  // A code file beside a session is not a document, and has nowhere to keep a
  // pairing (RFC 0339 3b).
  if (!isValidDocumentId(documentId)) return null
  if (await findPairing(repo, projectId, sessionId, documentId, true)) {
    return null
  }
  return pairSession(repo, projectId, {
    sessionId,
    documentId,
    createdBy: 'user',
  })
}

/**
 * The pairing that acts for a session on a document, or null: the document's
 * own first, then the project's leftovers.
 */
export async function activePairing(
  repo: Repo,
  projectId: string,
  sessionId: string,
  documentId: string,
): Promise<Pairing | null> {
  const home = await findPairing(repo, projectId, sessionId, documentId, false)
  return home?.pairing ?? null
}

/**
 * Every enabled pairing to a document: the document's own, then any the
 * project still holds for it, once each.
 */
export async function documentPairings(
  repo: Repo,
  projectId: string,
  documentId: string,
): Promise<Pairing[]> {
  const doc = await readyHandle<DocumentDoc>(repo, documentId)
  const project = await readyHandle<ProjectDoc>(repo, projectId)
  const out = new Map<string, Pairing>()
  for (const p of projectPairings(project?.doc(), { documentId })) {
    out.set(p.id, p)
  }
  for (const p of projectPairings(doc?.doc(), { documentId })) out.set(p.id, p)
  return [...out.values()].sort((a, b) => a.createdAt - b.createdAt)
}

/**
 * Why this session may not write this document, or null when it may (§3b).
 *
 * THE BOUNDARY LIVES IN THE WRITE, not in the tool list. The RFC first said a
 * turn with `mayEdit` off has no write tools, and the code disagrees twice: a
 * harness captures a session's tool list once (Claude Code's MCP server is
 * built per session, not per turn), and `mayEdit` is per DOCUMENT while a tool
 * set is per session, so one session paired to two documents cannot both have
 * and lack `patch_document`. Checked here, at the moment of the write, against
 * the live document, the boundary holds whatever the prompt says, holds on a
 * resumed session, and a toggle takes effect on the very next call.
 *
 * Read off the document (RFC 0339 3b); `projectId` is only searched for a
 * pairing written before the move.
 *
 * An unpaired document is not refused: pairing adds a boundary, it does not
 * take away what a session could already do.
 */
export async function mayNotEditReason(
  repo: Repo,
  projectId: string,
  sessionId: string | undefined,
  documentId: string,
): Promise<string | null> {
  if (!sessionId || !documentId) return null
  const pairing = await activePairing(repo, projectId, sessionId, documentId)
  if (!pairing || pairing.mayEdit) return null
  return (
    `Refused: you are paired to document ${documentId} and may not edit it. ` +
    'Only the person can turn editing on. Say what you would change, in the ' +
    'thread or as a comment, and leave the document as it is.'
  )
}
